import { Colors, Icon, Text } from '@amelio/ui-library';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { AiPromptIntegrationMode, AiPromptIntegrationConfig } from '../AiPromptModal';

interface AiPromptIntegrationStepProps {
    config: AiPromptIntegrationConfig;
    onIntegrate: (mode: AiPromptIntegrationMode) => void;
    /** When false, "Add" is disabled — there's nothing to add to, so it would behave like "Replace". */
    canAdd?: boolean;
    /** Why "Add" is unavailable for this result, shown under it (and read with it) when set. */
    addBlockedMessage?: string;
    /** Shown above the choice: what the manager should know about the result before integrating it. */
    review?: ReactNode;
    dataTestId: string;
}

export const AiPromptIntegrationStep = ({
    config,
    onIntegrate,
    canAdd = true,
    addBlockedMessage,
    review,
    dataTestId,
}: AiPromptIntegrationStepProps) => {
    const { t } = useTranslation();
    const addBlockedMessageId = useId();

    return (
        <div className={'animate-fade-in-up flex flex-col gap-5'}>
            <div className={'mb-4'}>
                <Text
                    weight={'semibold'}
                    className={'text-[20px] text-gray-800 md:text-[22px]'}
                >
                    {t(config.titleKey)}
                </Text>
            </div>

            {review}

            <div className={'flex flex-col gap-8'}>
                <button
                    type={'button'}
                    onClick={() => onIntegrate('add')}
                    disabled={!canAdd}
                    aria-describedby={addBlockedMessage ? addBlockedMessageId : undefined}
                    data-testid={`${dataTestId}-add-option`}
                    className={
                        'group rounded-2xl border-2 border-slate-200 bg-white p-7 text-left shadow-sm transition enabled:hover:border-violet-500/50 enabled:hover:bg-slate-100 enabled:hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50'
                    }
                >
                    <div className={'flex flex-col items-start gap-3'}>
                        <div className={'flex items-center gap-4'}>
                            <div
                                className={
                                    'flex h-12 w-12 items-center justify-center rounded-full bg-violet-500/10 group-hover:bg-violet-500'
                                }
                            >
                                <Icon
                                    name={'essential-add'}
                                    size={'xl'}
                                    color={Colors.SECONDARY_700}
                                    className={'group-hover:!text-white'}
                                />
                            </div>

                            <Text
                                weight={'bold'}
                                className={'text-xl text-gray-800 group-hover:!text-violet-500'}
                            >
                                {t(config.add.titleKey)}
                            </Text>
                        </div>

                        <div>
                            <Text
                                color={Colors.BASE_400}
                                className={'pl-16 text-base'}
                            >
                                {t(config.add.descriptionKey)}
                            </Text>
                        </div>
                    </div>
                </button>

                {addBlockedMessage && (
                    <div
                        id={addBlockedMessageId}
                        className={'-mt-5 flex items-start gap-2'}
                        data-testid={`${dataTestId}-add-blocked-message`}
                    >
                        <Icon
                            name={'essential-info-circle'}
                            size={'sm'}
                            color={Colors.BASE_500}
                            ariaHidden
                        />

                        <Text
                            size={'small'}
                            color={Colors.BASE_600}
                        >
                            {addBlockedMessage}
                        </Text>
                    </div>
                )}

                <button
                    type={'button'}
                    onClick={() => onIntegrate('replace')}
                    data-testid={`${dataTestId}-replace-option`}
                    className={
                        'group rounded-2xl border-2 border-slate-200 bg-white p-7 text-left shadow-sm transition hover:border-teal-400/50 hover:bg-slate-100 hover:shadow-lg'
                    }
                >
                    <div className={'flex flex-col items-start gap-3'}>
                        <div className={'flex items-center gap-4'}>
                            <div
                                className={
                                    'flex h-12 w-12 items-center justify-center rounded-full bg-teal-400/10 group-hover:bg-teal-400'
                                }
                            >
                                <Icon
                                    name={'arrow-refresh-circle'}
                                    size={'xl'}
                                    color={Colors.PRIMARY_400}
                                    className={'group-hover:!text-white'}
                                />
                            </div>

                            <Text
                                weight={'bold'}
                                className={'text-xl text-gray-800 group-hover:!text-teal-400'}
                            >
                                {t(config.replace.titleKey)}
                            </Text>
                        </div>

                        <div>
                            <Text
                                color={Colors.BASE_400}
                                className={'pl-16 text-base'}
                            >
                                {t(config.replace.descriptionKey)}
                            </Text>
                        </div>
                    </div>
                </button>
            </div>
        </div>
    );
};
